// Receiver side of the C1 "leave group" flow + S3 stranded-conversation
// recovery surfacing. Polled after sync/reconcile.
//
// When a peer leaves, they broadcast a self-Remove PROPOSAL (MLS forbids
// committing your own removal). OpenMLS buffers it on every remaining member;
// SOMEONE has to commit it to actually evict the leaver. We commit any
// conversation the SDK reports as having a pending proposal.
//
// Coordination: we don't elect a single committer. If several remaining members
// commit on the same tick, the SDK's send-then-merge rollback makes all but the
// first a no-op (the losers get `epoch_advanced`, roll back cleanly, and on the
// next sync the proposal has cleared — see `commit_pending_proposals` in the
// SDK). That keeps the flow correct without leader-election; the only cost is a
// few redundant round-trips per leave, and "leave" is an infrequent event. This
// also avoids the deadlock a "lowest-leaf commits" rule would hit when the
// lowest-leaf member IS the leaver (who has already deleted the conversation
// locally and isn't polling).

import { log } from '@core/log/log';

import { setStrandedConversations } from './strandedConversationsStore';

interface PendingProposalClient {
  conversationsWithPendingProposals?(): Promise<Uint8Array[]>;
  commitPendingProposals?(id: Uint8Array): Promise<void>;
  strandedConversations?(): Promise<Uint8Array[]>;
}

function hex(id: Uint8Array): string {
  let s = '';
  for (const b of id) s += b.toString(16).padStart(2, '0');
  return s;
}

/**
 * Commit any buffered pending proposals (peer leaves) so the leaver is actually
 * evicted, and surface stranded conversations for recovery. Best-effort and
 * idempotent — safe to call on every sync tick.
 */
export async function commitPendingProposalsAndSurfaceStranded(
  client: unknown,
): Promise<void> {
  const c = client as PendingProposalClient;
  if (typeof c.conversationsWithPendingProposals === 'function' &&
      typeof c.commitPendingProposals === 'function') {
    try {
      const pending = await c.conversationsWithPendingProposals();
      for (const convId of pending) {
        try {
          await c.commitPendingProposals(convId);
          log.info({ event: 'leave.commitPending.ok', conversationIdPrefix: hex(convId).slice(0, 8) });
        } catch (err) {
          // epoch_advanced (another member committed) is expected and benign;
          // the SDK rolled back and the proposal clears on next sync.
          log.debug({
            event: 'leave.commitPending.skip',
            conversationIdPrefix: hex(convId).slice(0, 8),
            error: (err as Error).message,
          });
        }
      }
    } catch (err) {
      log.warn({ event: 'leave.commitPending.pollFailed', error: (err as Error).message });
    }
  }

  // S3: a stranded conversation missed a Commit and can't advance locally. The
  // App reconcile re-subscribes (re-Welcome) which recovers it; here we just
  // surface it for observability so it's not a silent black hole.
  if (typeof c.strandedConversations === 'function') {
    try {
      const stranded = await c.strandedConversations();
      // Surface to the chat room so the user gets a "Restart secure session"
      // banner instead of a silent black hole (iOS parity).
      setStrandedConversations(stranded.map((id) => hex(id)));
      if (stranded.length > 0) {
        log.warn({
          event: 'sync.stranded.detected',
          count: stranded.length,
          conversationIdPrefixes: stranded.map((id) => hex(id).slice(0, 8)),
        });
      }
    } catch (err) {
      log.debug({ event: 'sync.stranded.pollFailed', error: (err as Error).message });
    }
  }
}
